import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { MapPin, Bookmark, BookmarkCheck, FileSearch, Star, CheckCircle, Clock, Users, Filter, RefreshCw, TrendingUp, List, Plus, FolderOpen } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PLACEHOLDER_IMG = "https://static.prod-images.emergentagent.com/jobs/1bc1e0bc-6716-4e3a-9c11-cd3b13fbaefa/images/1c30bf8bedb2266f7b8ce4ba5eca516cc891a4de6141fdb5b7fea0d0ccfddc2e.png";
const POSITIONS = ["", "Goalkeeper", "Centre-Back", "Left-Back", "Right-Back", "Defensive Midfielder", "Central Midfielder", "Attacking Midfielder", "Left Winger", "Right Winger", "Striker", "Centre-Forward"];

export default function ScoutDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("directory");
  const [profiles, setProfiles] = useState([]);
  const [recentlyImproved, setRecentlyImproved] = useState([]);
  const [savedProfiles, setSavedProfiles] = useState([]);
  const [savedSlugs, setSavedSlugs] = useState(new Set());
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [position, setPosition] = useState("");
  const [country, setCountry] = useState("");
  const [featured, setFeatured] = useState(false);
  const [verified, setVerified] = useState(false);
  const [shortlists, setShortlists] = useState([]);
  const [newListName, setNewListName] = useState("");
  const [showCreateList, setShowCreateList] = useState(false);
  const [activeShortlist, setActiveShortlist] = useState(null);
  const [shortlistProfiles, setShortlistProfiles] = useState([]);

  const loadDirectory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: "1", limit: "50" });
      if (position) params.append("position", position);
      if (country) params.append("country", country);
      if (featured) params.append("featured", "true");
      const { data } = await api.get(`/directory?${params}`);
      let results = data.profiles || [];
      if (verified) results = results.filter((p) => p.is_reviewed);
      setProfiles(results.sort((a, b) => new Date(b.updated_at || b.published_at || 0) - new Date(a.updated_at || a.published_at || 0)));
      setTotal(results.length);
    } catch {}
    setLoading(false);
  }, [position, country, featured, verified]);

  const loadSaved = useCallback(async () => {
    try {
      const { data } = await api.get("/scout/saved-players");
      setSavedProfiles(data.profiles || []);
      setSavedSlugs(new Set((data.saved || []).map((s) => s.slug)));
    } catch {}
  }, []);

  const loadRecentlyImproved = useCallback(async () => {
    try {
      const { data } = await api.get("/directory/recently-improved");
      setRecentlyImproved(data.profiles || []);
    } catch {}
  }, []);

  const loadShortlists = useCallback(async () => {
    try {
      const { data } = await api.get("/scout/shortlists");
      setShortlists(data.shortlists || []);
    } catch {}
  }, []);

  const loadShortlistDetail = useCallback(async (listId) => {
    try {
      const { data } = await api.get(`/scout/shortlists/${listId}`);
      setShortlistProfiles(data.profiles || []);
      setActiveShortlist(data.shortlist);
    } catch {}
  }, []);

  useEffect(() => {
    loadDirectory();
    loadSaved();
    loadRecentlyImproved();
    loadShortlists();
  }, [loadDirectory, loadSaved, loadRecentlyImproved, loadShortlists]);

  const toggleSave = async (slug) => {
    if (savedSlugs.has(slug)) {
      await api.delete(`/scout/save-player/${slug}`).catch(() => {});
      setSavedSlugs((prev) => { const s = new Set(prev); s.delete(slug); return s; });
      toast.success("Player removed");
    } else {
      await api.post("/scout/save-player", { slug }).catch(() => {});
      setSavedSlugs((prev) => new Set(prev).add(slug));
      toast.success("Player saved");
    }
    loadSaved();
  };

  const markForReview = async (slug) => {
    await api.post("/scout/mark-review", { slug }).catch(() => {});
    toast.success("Marked for review");
  };

  const addToShortlist = async (listId, slug) => {
    try {
      await api.post(`/scout/shortlists/${listId}/add`, { slug });
      toast.success("Added to shortlist");
      loadShortlists();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const createShortlist = async () => {
    if (!newListName.trim()) return;
    try {
      await api.post("/scout/shortlists", { name: newListName.trim() });
      toast.success("Shortlist created");
      setNewListName("");
      setShowCreateList(false);
      loadShortlists();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const getImg = (p) => p.profile_image_path ? `${BACKEND_URL}/api/media/${p.profile_image_path}` : PLACEHOLDER_IMG;

  const PlayerRow = ({ p }) => {
    const [showListMenu, setShowListMenu] = useState(false);
    const id = p.onboarding_data?.identity || {};
    const gen = p.generated_profile || {};
    return (
      <div className="card-dark p-4 flex items-center gap-4" data-testid={`scout-player-${p.slug}`}>
        <Link to={`/player/${p.slug}`} className="w-14 h-14 flex-shrink-0 overflow-hidden border border-[#1A1A1A]">
          <img src={getImg(p)} alt={p.name} className="w-full h-full object-cover" />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <Link to={`/player/${p.slug}`} className="text-sm font-bold text-[#F5F5F5] uppercase tracking-tight hover:text-[#D4AF37] transition-colors">{p.name}</Link>
            {p.is_reviewed && <CheckCircle size={12} className="text-[#D4AF37]" />}
            {p.is_featured && <Star size={12} className="text-[#D4AF37]" />}
          </div>
          <div className="text-xs text-[#777] flex flex-wrap gap-x-2">
            {id.primary_position && <span className="text-[#D4AF37]">{id.primary_position}</span>}
            {id.age && <span>{id.age} yrs</span>}
            {id.nationality && <span className="flex items-center gap-0.5"><MapPin size={9} />{id.nationality}</span>}
            {id.current_club && <span>{id.current_club}</span>}
          </div>
          {gen.headline && <p className="text-xs text-[#666] mt-1 truncate">{gen.headline}</p>}
        </div>
        <div className="flex gap-1.5 flex-shrink-0 relative">
          <button onClick={() => toggleSave(p.slug)} className={`p-2 rounded-sm border transition-colors ${savedSlugs.has(p.slug) ? "border-[#D4AF37]/30 text-[#D4AF37]" : "border-[#262626] text-[#666] hover:text-[#D4AF37]"}`} title={savedSlugs.has(p.slug) ? "Remove" : "Save"} data-testid={`save-${p.slug}`}>
            {savedSlugs.has(p.slug) ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
          </button>
          <div className="relative">
            <button onClick={() => setShowListMenu(!showListMenu)} className="p-2 rounded-sm border border-[#262626] text-[#666] hover:text-[#D4AF37] transition-colors" title="Add to shortlist" data-testid={`shortlist-${p.slug}`}>
              <List size={14} />
            </button>
            {showListMenu && (
              <div className="absolute right-0 top-full mt-1 bg-[#1A1A1A] border border-[#262626] rounded-sm shadow-lg z-10 min-w-[180px]" data-testid="shortlist-menu">
                <div className="px-3 py-2 border-b border-[#262626]">
                  <span className="text-[9px] font-bold text-[#888] uppercase tracking-wider">ADD TO SHORTLIST</span>
                </div>
                {shortlists.length === 0 && <div className="px-3 py-2 text-[10px] text-[#666]">No shortlists yet</div>}
                {shortlists.map((sl) => (
                  <button key={sl.id} onClick={() => { addToShortlist(sl.id, p.slug); setShowListMenu(false); }} className="w-full px-3 py-2 text-left text-[10px] text-[#ccc] hover:bg-[#262626] hover:text-[#D4AF37] transition-colors flex items-center gap-2">
                    <FolderOpen size={10} /> {sl.name} <span className="text-[#555]">({sl.player_slugs?.length || 0})</span>
                  </button>
                ))}
                <button onClick={() => { setShowCreateList(true); setShowListMenu(false); }} className="w-full px-3 py-2 text-left text-[10px] text-[#D4AF37] hover:bg-[#262626] transition-colors flex items-center gap-2 border-t border-[#262626]">
                  <Plus size={10} /> New Shortlist
                </button>
              </div>
            )}
          </div>
          <button onClick={() => markForReview(p.slug)} className="p-2 rounded-sm border border-[#262626] text-[#666] hover:text-[#D4AF37] transition-colors" title="Mark for review" data-testid={`review-${p.slug}`}>
            <FileSearch size={14} />
          </button>
        </div>
      </div>
    );
  };

  const displayProfiles = tab === "saved" ? savedProfiles : tab === "circulation" ? profiles.filter(p => p.is_circulation_candidate) : tab === "improved" ? recentlyImproved : tab === "shortlists" ? shortlistProfiles : profiles;

  return (
    <div className="bg-[#0A0A0A] min-h-screen py-12" data-testid="scout-dashboard">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="mb-8">
          <div className="overline mb-2">SCOUT DASHBOARD</div>
          <h1 className="text-3xl font-black tracking-tighter uppercase text-[#F5F5F5]">PLAYER NETWORK</h1>
          <p className="text-sm text-[#777] mt-1">Welcome, {user?.name || user?.email}. Browse and save players.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 mb-6 border-b border-[#1A1A1A]">
          {[
            { id: "directory", label: "ALL PLAYERS", icon: Users },
            { id: "featured", label: "FEATURED", icon: Star },
            { id: "improved", label: "RECENTLY IMPROVED", icon: TrendingUp },
            { id: "circulation", label: "CIRCULATION", icon: RefreshCw },
            { id: "saved", label: `SAVED (${savedProfiles.length})`, icon: Bookmark },
            { id: "shortlists", label: `SHORTLISTS (${shortlists.length})`, icon: FolderOpen },
          ].map((t) => (
            <button key={t.id} onClick={() => { setTab(t.id); if (t.id === "featured") setFeatured(true); else setFeatured(false); }} className={`flex items-center gap-1.5 px-5 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${tab === t.id ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#666] hover:text-[#aaa]"}`} data-testid={`scout-tab-${t.id}`}>
              <t.icon size={14} />{t.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        {tab !== "saved" && (
          <div className="flex flex-col sm:flex-row gap-2 mb-6" data-testid="scout-filters">
            <select value={position} onChange={(e) => setPosition(e.target.value)} className="input-dark text-sm py-2 flex-1" data-testid="scout-filter-position">
              <option value="">All Positions</option>
              {POSITIONS.filter(Boolean).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <input type="text" value={country} onChange={(e) => setCountry(e.target.value)} className="input-dark text-sm py-2 flex-1" placeholder="Country..." data-testid="scout-filter-country" />
            <button onClick={() => setVerified(!verified)} className={`px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-all flex items-center gap-1 ${verified ? "bg-[#D4AF37] text-black" : "bg-[#1A1A1A] text-[#666] border border-[#262626]"}`} data-testid="scout-filter-verified">
              <CheckCircle size={12} />VERIFIED
            </button>
          </div>
        )}

        {/* Shortlists Management */}
        {tab === "shortlists" && (
          <div className="mb-6 space-y-3" data-testid="shortlists-panel">
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#888]">Your shortlists</span>
              <button onClick={() => setShowCreateList(true)} className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-wider hover:underline flex items-center gap-1" data-testid="create-shortlist-btn"><Plus size={10} /> New Shortlist</button>
            </div>
            {showCreateList && (
              <div className="flex gap-2" data-testid="create-shortlist-form">
                <input type="text" value={newListName} onChange={(e) => setNewListName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") createShortlist(); }} className="input-dark text-sm py-2 flex-1" placeholder="Shortlist name..." autoFocus data-testid="shortlist-name-input" />
                <button onClick={createShortlist} className="bg-[#D4AF37] text-black font-bold text-[10px] uppercase tracking-wider px-4 rounded-sm hover:bg-[#C5A059] transition-colors" data-testid="shortlist-create-confirm">CREATE</button>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {shortlists.map((sl) => (
                <button key={sl.id} onClick={() => loadShortlistDetail(sl.id)} className={`card-dark p-3 text-left flex items-center gap-3 ${activeShortlist?.id === sl.id ? "border-[#D4AF37]/50" : ""}`} data-testid={`shortlist-${sl.id}`}>
                  <FolderOpen size={16} className="text-[#D4AF37] flex-shrink-0" />
                  <div>
                    <div className="text-xs font-bold text-[#F5F5F5] uppercase tracking-tight">{sl.name}</div>
                    <div className="text-[10px] text-[#777]">{sl.player_slugs?.length || 0} player{(sl.player_slugs?.length || 0) !== 1 ? "s" : ""}</div>
                  </div>
                </button>
              ))}
            </div>
            {activeShortlist && <div className="text-[10px] text-[#D4AF37] font-bold uppercase tracking-wider mt-2">Showing: {activeShortlist.name}</div>}
          </div>
        )}

        {/* Results */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs text-[#555]">{displayProfiles.length} player{displayProfiles.length !== 1 ? "s" : ""}</span>
          <span className="text-xs text-[#555] flex items-center gap-1"><Clock size={10} />Recently updated</span>
        </div>

        {loading && tab !== "saved" ? (
          <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>
        ) : displayProfiles.length === 0 ? (
          <div className="text-center py-16 text-[#666]">{tab === "saved" ? "No saved players yet. Browse the directory and save players." : "No players found."}</div>
        ) : (
          <div className="space-y-2">
            {displayProfiles.map((p) => <PlayerRow key={p.slug} p={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}
